import { routeName } from '~/@common/constants'

import { getDomainPair, isSenderSide } from './integrationDomain'
import { migrateFromHashParam } from './migrateFromHashParam'
import { encodeMigrationHash } from './migrationHash'
import { isIntegrationBridgePath } from './routeGuards'
import { resolveSafeRedirectURI } from './safeRedirect'

// sender(통합 student-up) 흐름의 caller 응답 — initialize.ts 분기 결정용.
//   - continue   : student-up 그대로 stay (probe timeout / hash 없음 등)
//   - navigated  : window.location.replace 호출됨 → (receiver(통합 student) redirect / login fail-safe)
export type SenderHandlerResult = { action: 'continue' } | { action: 'navigated' }

declare global {
  interface Window {
    __INTEGRATION_PROBE_RESULT__?: boolean
    __INTEGRATION_PROBE_PROMISE__?: Promise<boolean>
  }
}

// ★ index.html 인라인 probe REQUESTER 가 채운 window cache 를 read.
export const probeReceiverDeployed = (): Promise<boolean> => {
  if (window.__INTEGRATION_PROBE_RESULT__ === true) return Promise.resolve(true)
  if (window.__INTEGRATION_PROBE_PROMISE__) return window.__INTEGRATION_PROBE_PROMISE__
  return Promise.resolve(false)
}

// ★ top-level sender(통합 student-up) sessionStorage 의 토큰 + user 식별자를 receiver(통합 student) 가 인식할 수 있는 형태로 hash 변환.
export const buildSessionTokenHash = (): string | null => {
  const accessToken = sessionStorage.getItem('accessToken')
  const refreshToken = sessionStorage.getItem('refreshToken')
  if (!accessToken || !refreshToken) return null

  const studentId = localStorage.getItem('studentId')
  const academyId = localStorage.getItem('academyId')
  if (!studentId || !academyId) return null

  return encodeMigrationHash({
    accessToken,
    refreshToken,
    studentId,
    academyId,
    isPersist: false, // sessionStorage 보유 = 비유지 로그인 — receiver 도 ss 모드 유지
  })
}

export const appendUserStateQuery = (url: URL): void => {
  const isInitialPassword = localStorage.getItem('isInitialPassword')
  if (isInitialPassword !== null) url.searchParams.set('isInitialPassword', isInitialPassword)

  const isPasswordChangedRequired = localStorage.getItem('isPasswordChangedRequired')
  if (isPasswordChangedRequired !== null) {
    url.searchParams.set('isPasswordChangedRequired', isPasswordChangedRequired)
  }
}

const redirectTopLevelSenderToReceiver = async (
  pair: NonNullable<ReturnType<typeof getDomainPair>>,
): Promise<SenderHandlerResult> => {
  // sender top-level ──probe 결과 확인──▶ receiver /integration/probe
  const isReceiverDeployed = await probeReceiverDeployed()
  if (!isReceiverDeployed) return { action: 'continue' } // receiver 미배포 → student-up 그대로 stay

  const targetUrl = new URL(
    window.location.pathname + window.location.search + window.location.hash,
    pair.iframeOrigin,
  )

  // ★ sessionStorage 토큰 fallback — iframe 으로 못 가져오는 ss 토큰을 hash 로 운반.
  //   localStorage 모드 사용자는 hash 동봉 안 함 (iframe pull 로 충분).
  const tokenHash = buildSessionTokenHash()
  if (tokenHash) {
    targetUrl.searchParams.set('hash', tokenHash)
    appendUserStateQuery(targetUrl)
  }

  window.location.replace(targetUrl.toString())
  return { action: 'navigated' }
}

// ★ 베이직 학생앱에서 ?hash=... 로 student-up.{root} 에 진입한 경우.
//   1) hash 페이로드 흡수 (migrateFromHashParam — token / user 메타 / orientation / loadInitialData)
//   2) probe 로 receiver 배포 확인
//   3) 배포 OK → safeRedirect(redirectURI) 또는 자기 path 로 student.{root} redirect
//   4) probe timeout → student-up stay + URL query 정리 (storage 에 토큰 있어 사용 가능 — fail-safe)
//   5) 마이그 자체 실패 → /login?reason=hash_migration_failed
const handleHashMigrationCase = async (
  urlParams: URLSearchParams,
  pair: ReturnType<typeof getDomainPair>,
): Promise<SenderHandlerResult> => {
  if (!pair) return { action: 'continue' }

  try {
    await migrateFromHashParam(urlParams)
  } catch {
    window.location.replace(`${routeName.login}?reason=hash_migration_failed`)
    return { action: 'navigated' }
  }

  // redirectURI 우선 — 없으면 자기 path 그대로
  const safeRedirect = resolveSafeRedirectURI(urlParams.get('redirectURI'))
  const targetPath = safeRedirect ?? window.location.pathname

  const isReceiverDeployed = await probeReceiverDeployed()
  if (isReceiverDeployed) {
    const targetUrl = new URL(targetPath, pair.iframeOrigin)
    if (window.location.hash) targetUrl.hash = window.location.hash
    window.location.replace(targetUrl.toString())
    return { action: 'navigated' }
  }

  // probe timeout — student-up stay. 자기 pathname 유지하되 hash / redirectURI query 만 정리
  //   (재발동 방지 + 새고 시 동일 path 보존). targetPath 는 student.* 의 path 라서 sender 도메인에 적용 ✗.
  const cleanParams = new URLSearchParams(window.location.search)
  cleanParams.delete('hash')
  cleanParams.delete('redirectURI')
  const cleanSearch = cleanParams.toString()
  const cleanUrl = `${window.location.origin}${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`
  window.history.replaceState({ path: cleanUrl }, '', cleanUrl)
  return { action: 'continue' }
}

export const handleStudentUpLogic = async (): Promise<SenderHandlerResult> => {
  if (window.self !== window.top) return { action: 'continue' }
  if (isIntegrationBridgePath(window.location.pathname)) return { action: 'continue' }
  // /integration/sync 자체는 receiver iframe 으로 진입한 케이스 — redirect 하지 않음
  if (window.location.pathname === routeName.integration.sync) return { action: 'continue' }
  // /integration/logout-clear 는 LOGOUT_CLEAR iframe 응답자 — top-level 진입 시 redirect 하지 않음
  if (window.location.pathname === routeName.integration.logoutClear) {
    return { action: 'continue' }
  }
  // /integration/probe 는 sender 가 띄운 invisible iframe 의 응답자 — top-level 진입 시에도 redirect 하지 않음
  if (window.location.pathname === routeName.integration.probe) return { action: 'continue' }
  // /integration/dev-reset 은 dev panel 이 띄운 iframe target — top-level 진입 시 redirect 하지 않음
  if (window.location.pathname === routeName.integration.devReset) {
    return { action: 'continue' }
  }

  // sender 측 판정 — isSenderSide() 가 환경별 (live / staging / dev / localhost) 자동 분기 + flag fallback.
  if (!isSenderSide()) return { action: 'continue' }

  const pair = getDomainPair()
  if (!pair) return { action: 'continue' }

  const urlParams = new URLSearchParams(window.location.search)

  // 베이직 학생앱에서 보낸 ?hash= 진입
  if (urlParams.has('hash')) {
    return handleHashMigrationCase(urlParams, pair)
  }

  return redirectTopLevelSenderToReceiver(pair)
}
